import {
    FC,
    ReactNode,
    ComponentType,
    UIEventHandler,
    Children,
    useMemo,
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import { InternalScrollCarouselProvider } from '../helpers/InternalScrollCarouselContext';
import { useScrollCarousel } from '../helpers/ScrollCarouselContext';
import { Arrow } from '../Arrow/Arrow';
import { CarouselItem } from '../CarouselItem/CarouselItem';
import { BackFill } from '../BackFill/BackFill';
import { Dots } from '../Dots/Dots';

import { Backfill, Animation, OnArrowClick } from '../types';

import styles from './InnerScrollCarousel.scss';

type GoToIndex = ({ index }: { index: number; animation: Animation }) => void;

const SCROLL_POSITION_MAP = {
    horizontal: 'scrollLeft',
    vertical: 'scrollTop',
} as const;

const SCROLL_FULL_SIZE_MAP = {
    horizontal: 'scrollWidth',
    vertical: 'scrollHeight',
} as const;

const SCROLL_VISIBLE_SIZE_MAP = {
    horizontal: 'clientWidth',
    vertical: 'clientHeight',
} as const;

const SCROLL_TO_MAP = {
    horizontal: 'left',
    vertical: 'top',
} as const;

//Threshold to account for scroll inconsistencies between browsers
const SCROLL_THRESHOLD = 2;

export const InnerScrollCarousel: FC<{
    children: ReactNode;
    orientation?: 'horizontal' | 'vertical';
    itemsToShow?: number;
    showArrows?: boolean;
    animation?: Animation;
    onArrowClick?: OnArrowClick;
    dataTn?: string;
    showDots?: boolean;
    DotComponent?: ComponentType;
    dotsWrapperClassName?: string;
}> = ({
    children,
    orientation = 'horizontal',
    itemsToShow = 1,
    showArrows = false,
    animation = 'slide',
    onArrowClick,
    dataTn,
    showDots = false,
    DotComponent,
}) => {
    const {
        currentIndex,
        setCurrentIndex: _setCurrentIndex,
        totalItems,
        largestItemsToShow,
        stepSize,
        isInfinite,
    } = useScrollCarousel();

    if (stepSize !== 1 && stepSize !== Math.floor(itemsToShow)) {
        throw new Error(
            'Caorusel only supports single item scroll (stepSize = 1) or full page scroll (stepSize = Math.floor(itemsToShow))'
        );
    }

    const carouselRef = useRef<HTMLDivElement>(null);
    const [isBackfillInit, setIsBackfillInit] = useState(false);
    const isCarouselInit = useRef(false);

    /**
     * Carousels with not full pages need to snap to the last item and perfrom end snap.
     * End snap scroll needs to be handled differently.
     */
    const isEndSnapScroll =
        stepSize === Math.floor(itemsToShow) &&
        Math.floor(itemsToShow) > 1 &&
        totalItems % stepSize !== 0;
    const isScrollable = totalItems > itemsToShow;

    const backfill = useMemo<Backfill>(() => {
        const show = isInfinite && isScrollable && isBackfillInit;

        let startCount = 0;
        let endCount = 0;

        if (show) {
            startCount = isEndSnapScroll
                ? Math.ceil(largestItemsToShow)
                : //if there are not enough items to fill the page use the totalItems
                  Math.min(totalItems, Math.floor(largestItemsToShow));

            endCount = Math.ceil(itemsToShow);
        }

        return { show, startCount, endCount };
    }, [
        itemsToShow,
        largestItemsToShow,
        isInfinite,
        isScrollable,
        isEndSnapScroll,
        totalItems,
        isBackfillInit,
    ]);
    const prevBackfillStartCountRef = useRef(backfill.startCount);

    const prevIndexRef = useRef(currentIndex);
    const setCurrentIndex = useCallback(
        (index: number): void => {
            _setCurrentIndex({ index, backfillStartCount: backfill.startCount });
            prevIndexRef.current = currentIndex;
        },
        [currentIndex, _setCurrentIndex, backfill.startCount]
    );

    const isGoToIndexTriggered = useRef(false);
    const goToIndex = useCallback<GoToIndex>(
        ({ index, animation: _animation }) => {
            const scrollContainer = carouselRef.current;
            if (!scrollContainer) {
                return;
            }

            isGoToIndexTriggered.current = true;

            const scrollVisibleSize = scrollContainer[SCROLL_VISIBLE_SIZE_MAP[orientation]];
            const currentScrollPosition = scrollContainer[SCROLL_POSITION_MAP[orientation]];
            const itemSize = scrollVisibleSize / itemsToShow;

            let adjustedIndex = index;

            const endIndex = totalItems - 1;

            const firstItemIndex = backfill.startCount;
            const lastItemIndex = endIndex + backfill.startCount;

            if (isInfinite) {
                const isEndSnapAdjustmentNeeded =
                    isEndSnapScroll &&
                    index - backfill.startCount >= endIndex - (totalItems % itemsToShow);

                if (isEndSnapAdjustmentNeeded) {
                    adjustedIndex -= itemsToShow - (totalItems % stepSize);
                    setCurrentIndex(index);
                } else if (index > lastItemIndex && prevIndexRef.current - totalItems > 0) {
                    //different handling when navigating end backfill items
                    scrollContainer[SCROLL_POSITION_MAP[orientation]] =
                        (prevIndexRef.current - totalItems) * itemSize;
                    adjustedIndex = index - totalItems;
                    setCurrentIndex(adjustedIndex);
                } else if (index === firstItemIndex && prevIndexRef.current === lastItemIndex) {
                    //different handling when index reset to first item happens
                    if (Math.abs(currentScrollPosition - index * itemSize) > SCROLL_THRESHOLD) {
                        //scroll to previos snap position only if scroll position is not already at the right position
                        scrollContainer[SCROLL_POSITION_MAP[orientation]] =
                            (prevIndexRef.current - totalItems) * itemSize;
                    }
                    adjustedIndex = index;
                    setCurrentIndex(adjustedIndex);
                } else if (index === 0) {
                    //different handling when navigating to first item
                    scrollContainer[SCROLL_POSITION_MAP[orientation]] =
                        (prevIndexRef.current + totalItems) * itemSize;
                    adjustedIndex = index + totalItems;
                    setCurrentIndex(adjustedIndex);
                } else if (prevIndexRef.current === 1 && index === totalItems) {
                    //different handling when index reset to last item happens
                    if (Math.abs(currentScrollPosition - index * itemSize) > SCROLL_THRESHOLD) {
                        //scroll to previos snap position only if scroll position is not already at the right position
                        scrollContainer[SCROLL_POSITION_MAP[orientation]] =
                            (prevIndexRef.current + totalItems) * itemSize;
                    }
                    adjustedIndex = index;
                    setCurrentIndex(adjustedIndex);
                } else {
                    adjustedIndex = index;
                    setCurrentIndex(adjustedIndex);
                }
            } else {
                setCurrentIndex(index);
            }

            const newScrollPosition = itemSize * adjustedIndex;

            if (Math.abs(currentScrollPosition - newScrollPosition) <= SCROLL_THRESHOLD) {
                //do not scroll if scroll position is already at the right position
                isGoToIndexTriggered.current = false;
                return;
            }

            if (_animation === 'none') {
                scrollContainer[SCROLL_POSITION_MAP[orientation]] = newScrollPosition;
            } else if (_animation === 'slide') {
                scrollContainer.scrollTo({
                    [SCROLL_TO_MAP[orientation]]: newScrollPosition,
                    behavior: 'smooth',
                });
            }
        },
        [
            orientation,
            backfill.startCount,
            itemsToShow,
            stepSize,
            totalItems,
            isEndSnapScroll,
            isInfinite,
            setCurrentIndex,
        ]
    );

    useLayoutEffect(() => {
        if (!isBackfillInit) {
            //backfill items are rendered only on client side, we need to skip one render cycle to avoid flickering
            setIsBackfillInit(true);
            return;
        }

        if (backfill.startCount && !isCarouselInit.current) {
            goToIndex({
                index: currentIndex === 0 ? backfill.startCount : currentIndex,
                animation: 'none',
            });
        } else if (prevBackfillStartCountRef.current !== backfill.startCount) {
            goToIndex({
                index: currentIndex - prevBackfillStartCountRef.current + backfill.startCount,
                animation: 'none',
            });
        } else {
            goToIndex({
                index: currentIndex,
                animation,
            });
        }
        isCarouselInit.current = true;
        prevBackfillStartCountRef.current = backfill.startCount;
    }, [backfill.show, backfill.startCount, currentIndex, goToIndex, animation, isBackfillInit]);

    //used to detect if scrolling action is finished
    const scrollTimeout = useRef<NodeJS.Timeout>();

    const onScroll = useCallback<UIEventHandler<HTMLDivElement>>(
        event => {
            const scrollContainer = event.currentTarget;

            const scrollPosition = scrollContainer[SCROLL_POSITION_MAP[orientation]];
            const scrollFullSize = scrollContainer[SCROLL_FULL_SIZE_MAP[orientation]];
            const scrollVisibleSize = scrollContainer[SCROLL_VISIBLE_SIZE_MAP[orientation]];

            const itemSize = scrollVisibleSize / itemsToShow;

            //intinite scroll handling
            if (isInfinite) {
                //aditional handling for fractional itemsToShow
                const itemsToShowRemainer = itemsToShow - Math.floor(itemsToShow);
                const remainingItemSize = itemsToShowRemainer
                    ? Math.ceil(itemSize * (1 - itemsToShowRemainer))
                    : 0;

                const isStart = isEndSnapScroll
                    ? scrollPosition <= remainingItemSize
                    : scrollPosition <= 0;
                if (isStart) {
                    //if we are at the beginning of the carousel, scroll to the end
                    scrollContainer[SCROLL_POSITION_MAP[orientation]] =
                        totalItems * itemSize + (isEndSnapScroll ? remainingItemSize : 0);
                }

                const isEnd =
                    scrollFullSize - scrollPosition - scrollVisibleSize - remainingItemSize <=
                    SCROLL_THRESHOLD;

                if (isEnd) {
                    //if we are at the end of the carousel, scroll back to the beginning
                    scrollContainer[SCROLL_POSITION_MAP[orientation]] =
                        itemSize * backfill.startCount;
                }
            }
            /**
             * Too complicated scroll snap detection logic
             * TODO: implement snap event listener when it is supported https://drafts.csswg.org/css-scroll-snap-2/#snap-events
             */
            let index = Math.round((scrollPosition + SCROLL_THRESHOLD) / itemSize);

            const isEndWithoutBackfill =
                scrollFullSize -
                    scrollPosition -
                    itemSize * backfill.endCount -
                    scrollVisibleSize <=
                SCROLL_THRESHOLD;

            //Adjust index when navigating to end snap item
            if (isEndSnapScroll && isEndWithoutBackfill) {
                index = totalItems - (totalItems % stepSize) + backfill.startCount;
            }

            if (scrollTimeout.current) {
                clearTimeout(scrollTimeout.current);
            }

            scrollTimeout.current = setTimeout(() => {
                if (
                    currentIndex !== index &&
                    /**
                     * Not infinite carousel has items that are impossible to scroll to. They can be active
                     * only by directly navigating to the item with goToIndex. Need to exclude this case in scroll
                     * handler since it causes inacurate active index.
                     */
                    !(!isInfinite && isEndWithoutBackfill && isGoToIndexTriggered.current)
                ) {
                    setCurrentIndex(index);
                }
                isGoToIndexTriggered.current = false;
            }, 50);
        },
        [
            orientation,
            isInfinite,
            itemsToShow,
            backfill.startCount,
            backfill.endCount,
            isEndSnapScroll,
            currentIndex,
            stepSize,
            totalItems,
            setCurrentIndex,
        ]
    );

    const contextValue = useMemo(
        () => ({
            carouselOrientation: orientation,
            itemsToShow,
            stepSize,
            showArrows,
            currentIndex,
            totalItems,
            isInfinite,
            backfill,
            isEndSnapScroll,
            setCurrentIndex,
            isScrollable,
            isBackfillInit,
            onArrowClick,
            showDots,
            DotComponent,
        }),
        [
            orientation,
            itemsToShow,
            stepSize,
            showArrows,
            totalItems,
            currentIndex,
            isInfinite,
            backfill,
            isEndSnapScroll,
            setCurrentIndex,
            isScrollable,
            isBackfillInit,
            onArrowClick,
            showDots,
            DotComponent,
        ]
    );

    return (
        <InternalScrollCarouselProvider value={contextValue}>
            <div className={`${styles.wrapper} ${styles[orientation]}`} data-tn={dataTn}>
                <Arrow type="previous" />

                <div
                    ref={carouselRef}
                    className={`${styles.carousel} ${styles[orientation]} ${styles[animation]}`}
                    onScroll={onScroll}
                >
                    <BackFill type="start">{children}</BackFill>

                    {Children.map(children, (child, index) => (
                        <CarouselItem
                            key={index}
                            snapIndex={index}
                            trueIndex={index + backfill.startCount}
                        >
                            {child}
                        </CarouselItem>
                    ))}

                    <BackFill type="end">{children}</BackFill>
                </div>

                <Arrow type="next" />
            </div>

            <Dots />
        </InternalScrollCarouselProvider>
    );
};
